import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../shared/context/AuthContext'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'
import { httpClient } from '../../shared/api/httpClient'
import { BANNER_CONSTANTS } from './bannerConstants'

/**
 * Port of `app/banner/bannerController.js` (AngularJS `bannerController`)
 * data-fetching logic to TanStack Query, per task 014's requirements.
 *
 * `resolveEnv`/`resolveEnvLabel` are ported 1:1 from the legacy controller's
 * module-private helpers of the same name.
 */
export function resolveEnv(urlAstrea) {
  return BANNER_CONSTANTS.HOMOLOG_URLS.includes(urlAstrea)
    ? BANNER_CONSTANTS.ENVS.HOMOLOG
    : BANNER_CONSTANTS.ENVS.PRODUCTION
}

export function resolveEnvLabel(env, urlAstrea) {
  return BANNER_CONSTANTS.ENV_LABELS[env] || urlAstrea
}

/**
 * Equivalent of the legacy `initView()`: resolves the current env from the
 * fixed Astrea environment (`useAstreaEnvironment()`'s `urlAstrea`, no longer
 * a per-user session value — see that hook's doc-comment), fetches
 * `GET /banner/info/:env` (`server_app/routes/bannerRouter.js`), and
 * derives the public banner image URL + env label the same way the legacy
 * controller did (`$scope.currentBannerUrl`, `$scope.currentEnvName`).
 *
 * The legacy code appended `?t=<timestamp>` to the image URL once per
 * `initView()` call (i.e. once per state entry / `$state.reload()`) purely
 * to cache-bust the `<img>` tag after a fresh upload. The query's own
 * `dataUpdatedAt` (bumped on every successful fetch, including the
 * `$state.reload()`-equivalent refetch this hook's callers trigger after a
 * mutation — see `useBannerMutation` below) is reused for the same
 * cache-busting purpose instead of a separate `Date.now()` call, so the
 * value only changes when there is actually new data to show.
 */
export function useBannerQuery() {
  const { urlAstrea } = useAstreaEnvironment()
  const env = resolveEnv(urlAstrea)
  const envLabel = resolveEnvLabel(env, urlAstrea)

  const query = useQuery({
    queryKey: ['banner', env],
    queryFn: async () => {
      const response = await httpClient.get(`/banner/info/${env}`, { withCredentials: true })
      return response.data
    },
    enabled: !!urlAstrea,
  })

  const cacheBust = query.dataUpdatedAt || 0
  const currentBannerUrl = `${BANNER_CONSTANTS.BASE_URL}/astrea-banner-login-${env}.webp?t=${cacheBust}`

  return {
    env,
    envLabel,
    currentInfo: query.data,
    currentBannerUrl,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

/**
 * Equivalent of the legacy `executeUploadBanner()`: `POST /banner/upload`
 * with a `FormData` payload (`banner` file + `callToAction`/`campaign`/`env`/
 * `who`). `who` mirrors `resolveWho()` (`AuthService.getUserName() ||
 * AuthService.getUserLogin()`).
 *
 * The legacy success callback also fired a fire-and-forget
 * `$http.post('/statusLogs/create', ...)` (`saveStatusLog('UPLOAD')`) before
 * showing the toast and reloading the state. That endpoint
 * (`server_app/routes/...`) is unrelated to the banner module itself (it's
 * a generic status-log write shared by several legacy controllers) and is
 * out of this task's scope to re-introduce here — see the Notas de Execução
 * in `014-module-banner.md` for the explicit decision.
 */
export function useBannerUploadMutation() {
  const queryClient = useQueryClient()
  const { getUserName, getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async ({ file, callToAction, campaign, env }) => {
      const formData = new FormData()
      formData.append('banner', file)
      formData.append('callToAction', callToAction)
      formData.append('campaign', campaign)
      formData.append('env', env)
      formData.append('who', getUserName() || getUserLogin())

      const response = await httpClient.post('/banner/upload', formData, { withCredentials: true })
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['banner', variables.env] })
    },
  })
}

/**
 * Equivalent of the legacy `executeRemoveBanner()`: `DELETE
 * /banner/remove/:env?who=...`.
 */
export function useBannerRemoveMutation() {
  const queryClient = useQueryClient()
  const { getUserName, getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async ({ env }) => {
      const who = getUserName() || getUserLogin()
      const response = await httpClient.delete(
        `/banner/remove/${env}?who=${encodeURIComponent(who || '')}`,
        { withCredentials: true },
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['banner', variables.env] })
    },
  })
}
